import { useEffect, useState } from "react";
import { Send } from "lucide-react";
import { toast } from "sonner";
import { errorMessage } from "../../../../api/client";
import { twilioApi, type TestText, type TwilioView } from "../../../../api/twilio";
import { Button } from "../../../../components/ui";
import { Card, when } from "./parts";

const POLL_MS = 3000;
const POLL_MAX = 40;

function StatusLine({ t }: { t: TestText }) {
  const label = t.error_code ? `${t.delivery_status ?? t.status} · error ${t.error_code}` : t.delivery_status ?? t.status;
  const tone = t.error_code || ["failed", "undelivered"].includes(t.delivery_status ?? "") || t.status !== "SENT" ? "text-amber-200" : t.final ? "text-emerald-300" : "text-white";
  return (
    <div className="rounded-xl border border-white/10 p-3 text-sm" data-testid={`twilio-test-text-${t.id}`}>
      <p className="flex flex-wrap items-center gap-2"><span className="font-mono text-zinc-300">{t.to_masked}</span>
        <span className={`font-semibold ${tone}`} data-testid="twilio-test-text-status">{label}</span>
        {!t.final && <span className="text-xs text-zinc-500">updating…</span>}
        <span className="ml-auto text-xs text-zinc-500">{t.provider} · {when(t.sent_at)}</span></p>
      <p className="mt-1 text-zinc-400" data-testid="twilio-test-text-meaning">{t.meaning}</p>
    </div>
  );
}

export function TestTextCard({ v }: { v: TwilioView }) {
  const prod = v.environment === "production";
  const [to, setTo] = useState(prod ? "" : v.allowlist[0]?.phone_e164 ?? "");
  const [busy, setBusy] = useState(false);
  const [items, setItems] = useState<TestText[]>(v.test_texts);
  const live = items.find((t) => !t.final);
  useEffect(() => {
    if (!live) return;
    let n = 0;
    const id = window.setInterval(async () => {
      n += 1;
      try { const u = await twilioApi.testStatus(live.id); setItems((xs) => xs.map((x) => (x.id === u.id ? u : x))); if (u.final || n >= POLL_MAX) window.clearInterval(id); }
      catch { window.clearInterval(id); }
    }, POLL_MS);
    return () => window.clearInterval(id);
  }, [live?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const send = async () => {
    setBusy(true);
    try { const t = await twilioApi.sendTest(to); setItems((xs) => [t, ...xs].slice(0, 5)); toast.success("Test text sent"); }
    catch (e) { toast.error(errorMessage(e)); } finally { setBusy(false); }
  };
  return (
    <Card title="Send test text" icon={<Send className="h-5 w-5 text-signal" />} testId="twilio-test-text-card">
      <p className="text-sm text-zinc-400">Sends “{v.test_body}” to {prod ? "any mobile number you enter" : "an allow-listed number"}. Provider now: <span className="font-mono text-zinc-200">{v.provider_mode}</span>. Limit 5 every 10 minutes.</p>
      <div className="mt-4 flex flex-wrap items-center gap-3">
        {prod ? (
          <input type="tel" value={to} onChange={(e) => setTo(e.target.value)} placeholder="+1 555 555 0100" aria-label="Send test text to" data-testid="twilio-test-text-to"
            className="h-11 rounded-[12px] border border-white/15 bg-white/[0.045] px-3 text-[15px] text-white focus:border-signal focus:outline-none" />
        ) : <select value={to} onChange={(e) => setTo(e.target.value)} aria-label="Send test text to" data-testid="twilio-test-text-to"
          className="h-11 rounded-[12px] border border-white/15 bg-white/[0.045] px-3 text-[15px] text-white focus:border-signal focus:outline-none">
          {v.allowlist.map((a) => <option key={a.phone_e164} value={a.phone_e164} className="bg-ink">{a.phone_e164}</option>)}
        </select>}
        <Button onClick={send} loading={busy} disabled={!to} data-testid="twilio-send-test-text">Send test text</Button>
      </div>
      <div className="mt-4 space-y-2" data-testid="twilio-test-text-list">{items.map((t) => <StatusLine key={t.id} t={t} />)}</div>
    </Card>
  );
}
